/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import React from 'react';

import { Icon, Label, Step, StepGroup } from '@mairistem/composants';

import type { AssistantStep, Steps } from '@/types';
import { COLORS } from '@/constants';
import { AssistantContext } from '@/contexts';

import { getStepPosition } from './stepNavigation';

interface Props {
  /** Index de l'étape actuelle */
  currentStepIndex: number;
  /** Étapes de l'assistant pour le type d'acte sélectionné (métadonnées) */
  steps: Partial<Record<Steps, AssistantStep>>;
  /**
   * Étapes du type d'acte sélectionné, dans l'ORDRE réel du parcours. Sert à
   * numéroter les étapes par POSITION (1, 2, 3…) plutôt que par valeur
   * numérique brute de `Steps` : deux types d'acte peuvent avoir des étapes
   * différentes (ex. reconnaissance a une étape `Enfant` que la naissance
   * n'a pas), donc les valeurs de `Steps` ne sont pas forcément consécutives
   * pour un type donné.
   */
  orderedSteps: Steps[];
  /** Callback appelé lors du changement d'étape */
  onStepChange: (stepIndex: Steps) => void;
}

const AssistantHeader = (props: Props) => {
  // #region Declarations
  const { currentStepIndex, steps, orderedSteps, onStepChange } = props;

  const { errors } = React.useContext(AssistantContext);

  const currentPosition = getStepPosition(orderedSteps, currentStepIndex);

  // #endregion

  // #region Component

  return (
    <StepGroup
      fluid
      className="connected"
      style={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none' }}
    >
      {orderedSteps.map((stepIndex: Steps, position: number) => {
        const step = steps[stepIndex];

        if (!step) {
          return null;
        }

        const { title, dataTestId, validated = false } = step;

        const errorsStep = errors?.[stepIndex];

        // TODO : lors de la gestion des erreurs, adapter cette partie de code
        // if (!_.isEmpty(errorsStep)) {
        //   content = (
        //     <Icon
        //       fitted
        //       color="red"
        //       size="big"
        //       name="times circle"
        //     />
        //   );
        // }

        const content: React.ReactNode = (
          <Label
            circular
            size="big"
            style={{
              backgroundColor:
                position <= currentPosition ? COLORS.Background : 'white',
              border: `solid 1px ${COLORS.HeaderStepBorder}`,
              color: position <= currentPosition ? 'white' : COLORS.Background,
            }}
          >
            {position + 1}
            {(validated || position < currentPosition) &&
              _.isEmpty(errorsStep) && (
                <Icon
                  name="checkmark"
                  style={{
                    position: 'absolute',
                    top: '40%',
                    left: '55%',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    color: 'green',
                    width: 17,
                    height: 20,
                  }}
                />
              )}
            {!_.isEmpty(errorsStep) && position < currentPosition && (
              <Icon
                name="close"
                style={{
                  position: 'absolute',
                  top: '40%',
                  left: '53%',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  color: 'red',
                  width: 17,
                  height: 20,
                }}
              />
            )}
          </Label>
        );

        return (
          <Step
            key={title}
            style={{
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
            data-testid={dataTestId}
            onClick={() => onStepChange(stepIndex)}
          >
            <Step.Content style={{ bottom: '0.6em' }}>
              {content}
              <Step.Title style={{ paddingTop: 5, color: COLORS.Background }}>
                {title}
              </Step.Title>
            </Step.Content>
          </Step>
        );
      })}
    </StepGroup>
  );
};

export default AssistantHeader;
